import { ApiaUtil, ApiaUtilModalHandler } from '@apia/components';
import { SimpleButton } from '../../../common';
import { useCallback, useRef } from 'react';
import FiltersSetterForm from './FiltersSetterComponents/FiltersSettersForm';
import FiltersSetterController from './FiltersSetterComponents/FiltersSettersController';
import dayjs from 'dayjs';
import { getLabel } from '../../../../utils/util';
import { observer } from 'mobx-react-lite';
import { usePrevious } from '@apia/util';
import queriesQueue from '../../../../store/querySlice/queueController';

export interface DateInputProps {
  chkFromStartOfValue: boolean;
  fromStartOfValue: string;
  amountOf: number;
  fixedValue: string;
}

export type UserQryFilter = {
  userQryColumnFilters: {
    userQryColumnFilter:
      | {
          type: string;
          value: string;
          columnFilterName: string;
          columnFilterId: string;
          columnId: string;
        }
      | {
          type: string;
          value: string;
          columnFilterId: string;
          columnFilterName: string;
          columnId: string;
        }[];
  };
  name: string;
  id: string;
};

export type UserQryFilters = {
  userQryFilter: UserQryFilter | UserQryFilter[];
};

export const calculateDate = (props: DateInputProps): string => {
  const { chkFromStartOfValue, fromStartOfValue, amountOf, fixedValue } = props;

  // Get the date format from the global variable
  const dateFormat = window.strDateFormat.toLocaleUpperCase();

  const now = dayjs();

  if (!chkFromStartOfValue) {
    // Return the fixed value if chkFromStartOfValue is false
    return fixedValue;
  }

  // Handle the case when chkFromStartOfValue is true
  switch (fromStartOfValue) {
    case 'actualDay':
      return now.startOf('day').format(dateFormat);
    case 'actualWeek':
      return now.startOf('week').format(dateFormat);
    case 'actualMonth':
      return now.startOf('month').format(dateFormat);
    case 'actualQuarter': {
      const currentMonth = now.month();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3; // Calculate the first month of the quarter
      return now.month(quarterStartMonth).startOf('month').format(dateFormat);
    }
    case 'actualSemester': {
      const currentMonth = now.month();
      const semesterStartMonth = currentMonth < 6 ? 0 : 6; // Calculate the first month of the semester
      return now.month(semesterStartMonth).startOf('month').format(dateFormat);
    }
    case 'actualYear':
      return now.startOf('year').format(dateFormat);
    case 'daysAmount':
      return now.subtract(amountOf, 'day').format(dateFormat);
    case 'weeksAmount':
      return now.subtract(amountOf, 'week').format(dateFormat);
    case 'monthsAmount':
      return now.subtract(amountOf, 'month').format(dateFormat);
    case 'quartersAmount': {
      const monthsToSubtract = amountOf * 3; // Each quarter equals 3 months
      return now.subtract(monthsToSubtract, 'month').format(dateFormat);
    }
    case 'semestersAmount': {
      const monthsToSubtract = amountOf * 6; // Each semester equals 6 months
      return now.subtract(monthsToSubtract, 'month').format(dateFormat);
    }
    case 'yearsAmount':
      return now.subtract(amountOf, 'year').format(dateFormat);
    default:
      throw new Error(`Invalid fromStartOfValue: ${fromStartOfValue}`);
  }
};

const FiltersSetter = observer((props: { type: string }) => {
  const newName = FiltersSetterController.getInstance().newFilterGroupName;
  const comboSelection =
    FiltersSetterController.getInstance().savedFilters.find((s) => s.selected);
  const disableConfirm =
    props.type === 'create' ? newName.trim().length === 0 : !comboSelection;

  const modalHandler = useRef<ApiaUtilModalHandler | null>(null);
  const lastCheck = usePrevious(disableConfirm);

  if (disableConfirm !== undefined && disableConfirm !== lastCheck.current) {
    modalHandler.current?.setConfirmProps({
      disabled: disableConfirm,
    });
  }
  const handleClick = useCallback(() => {
    const controller = FiltersSetterController.getInstance(
      undefined,
      undefined,
    );
    window.savedFiltersController = controller as any;
    controller.newFilterGroupName = '';
    modalHandler.current = ApiaUtil.instance.modals.open({
      children: <FiltersSetterForm controller={controller} type={props.type} />,
      size: 'xl-fixed',
      title: getLabel('lblFilters').text,
      shouldCloseOnOverlayClick: true,
      initialFocusGetter(modalInnerRef) {
        return modalInnerRef.querySelector('label');
      },
      onConfirm: async () => {
        const result = await queriesQueue.runAction(
          'directUpdateFilters',
          async () => {
            return controller.validate(props.type);
          },
          ['fromUpdateFilters', 'fromSearchfromUpdateFilters'],
        );
        return !!result;
      },
      confirmProps: {
        disabled: disableConfirm,
      },
    });
  }, [disableConfirm, props.type]);

  return (
    <SimpleButton variant="outline" onClick={handleClick}>
      {
        getLabel(
          props.type === 'create' ? 'lblCreateNewFilter' : 'lblSaveFilters',
        ).text
      }
    </SimpleButton>
  );
});

export default FiltersSetter;
